import { ResponsiveBar } from '@nivo/bar';
import { Theme } from '@nivo/core';
import clsx from 'clsx';
import { groupBy } from 'lodash';
import { FC, useMemo, useState } from 'react';
import Tab from 'src/components/0200_tab';
import {
  IItemTransactionDistributionEnum,
  IItemTransactionSourceEnum,
} from 'src/graphql/types';
import useEventItemTransactionStatistics from 'src/hooks/events/useEventItemTransactionStatistics';

const theme: Theme = {
  text: {
    fill: '#fff',
    fontFamily: 'Dax',
    fontSize: 14,
  },
  crosshair: {
    line: {
      stroke: '#ccc',
    },
  },
  grid: {
    line: {
      stroke: '#444',
      strokeWidth: 1,
    },
  },
  tooltip: {
    container: {
      background: '#333',
    },
  },
  axis: {
    legend: {
      text: {
        fontSize: 14,
      },
    },
    ticks: {
      text: {
        fontSize: 12,
        fill: '#ddd',
      },
    },
  },
  labels: {
    text: {
      fontSize: 12,
    },
  },
};

const Statistics: FC = () => {
  const { statistics } = useEventItemTransactionStatistics();
  const [source, setSource] = useState<'all' | 'econ' | 'mod'>('all');
  const itemGroup = useMemo(
    () =>
      groupBy(
        statistics.filter(x => {
          if (source === 'econ')
            return x.source === IItemTransactionSourceEnum.Crafting;
          if (source === 'mod')
            return x.source === IItemTransactionSourceEnum.ModReward;

          return true;
        }),
        'itemId',
      ),
    [source, statistics],
  );
  const nivoData = useMemo(
    () =>
      Object.values(itemGroup)
        .sort(
          (x, y) =>
            y.map(x => x.stack).reduce((a, b) => a + b, 0) -
            x.map(x => x.stack).reduce((a, b) => a + b, 0),
        )
        .map(x => ({
          itemName: x[0].itemName,
          'Econ Addition': x
            .filter(
              y =>
                y.distribution === IItemTransactionDistributionEnum.Generated &&
                y.source === IItemTransactionSourceEnum.Crafting,
            )
            .map(x => x.stack)
            .reduce((a, b) => a + b, 0),
          'Econ Removal': x
            .filter(
              y =>
                y.distribution === IItemTransactionDistributionEnum.Removed &&
                y.source === IItemTransactionSourceEnum.Crafting,
            )
            .map(x => x.stack)
            .reduce((a, b) => a + b, 0),
          'Mod Reward': x
            .filter(
              y =>
                y.distribution === IItemTransactionDistributionEnum.Generated &&
                y.source === IItemTransactionSourceEnum.ModReward,
            )
            .map(x => x.stack)
            .reduce((a, b) => a + b, 0),
          Usage: x
            .filter(
              y =>
                y.distribution === IItemTransactionDistributionEnum.Removed &&
                y.source === IItemTransactionSourceEnum.ModReward,
            )
            .map(x => x.stack)
            .reduce((a, b) => a + b, 0),
        })),
    [itemGroup],
  );

  return (
    <div>
      <div className="flex justify-start lg:justify-center border-b border-juno-gray-700">
        <div>
          <div className="text-xs uppercase">Transaction Source</div>
          <div className="flex item-center">
            <Tab
              label="All"
              highlightMode="horizontal"
              isActive={source === 'all'}
              onClick={() => setSource('all')}
            />
            <Tab
              label="Econ Sheets"
              highlightMode="horizontal"
              isActive={source === 'econ'}
              onClick={() => setSource('econ')}
            />
            <Tab
              label="Mod Rewards"
              highlightMode="horizontal"
              isActive={source === 'mod'}
              onClick={() => setSource('mod')}
            />
          </div>
        </div>
      </div>
      <div className="overflow-auto max-w-[100vw] lg:max-w-[768px] xl:max-w-[1024px]">
        <div
          style={{ width: Math.max(Object.keys(itemGroup).length * 28, 240) }}
          className="h-[640px]"
        >
          <ResponsiveBar
            enableTotals
            data={nivoData}
            keys={['Econ Addition', 'Econ Removal', 'Mod Reward', 'Usage']}
            colors={{ scheme: 'tableau10' }}
            indexBy="itemName"
            margin={{ top: 32, right: 32, bottom: 128, left: 32 }}
            theme={theme}
            labelSkipHeight={16}
            axisBottom={{
              tickRotation: -45,
              tickPadding: 12,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Statistics;
